import { formatDate } from '@findy-code/utils/date';
import { formatDecimal } from '@findy-code/utils/decimal';
import { sanitize } from '@findy-code/utils/sanitize';
import Head from 'next/head';

import type { FetchJobDescriptionDetailFragment } from '../../graphql/';

type Props = {
  jobDescription: FetchJobDescriptionDetailFragment;
  company: FetchJobDescriptionDetailFragment['company'];
};

/**
 * @todo Agrias同様、enumをfetchした体の実装に変更する。
 */
const basicSalaryEnumTmp = {
  MONTHLY: '月給',
  YEARLY: '年俸',
  NON_DISCLOSURE: '非開示',
};

const titleWithDescription = (title: string, description: string) => {
  if (!description || description.length === 0) return '';

  return `<p>▼${title}</p>
<p>${sanitize(description)}</p>
`;
};

const createJobDescriptionHtml = (props: Props) => {
  const { jobDescription: jd } = props;
  let html = '';
  html += titleWithDescription('仕事内容', jd.jobDescription);
  html += titleWithDescription('必須スキル/経験', jd.mustSkills ?? '');
  html += titleWithDescription('歓迎スキル/経験', jd.desiredSkills ?? '');
  html += titleWithDescription('求める人物像', jd.personality ?? '');
  html += titleWithDescription('仕事の魅力', jd.rewarding ?? '');
  html += titleWithDescription('開発環境', jd.developmentEnvironment ?? '');
  const minSalaryText = `${formatDecimal(jd.basicSalaryMin)}円 ~`;
  const maxSalaryText = jd.basicSalaryMax
    ? ` ${formatDecimal(jd.basicSalaryMax)}円`
    : '';
  const salaryText = jd.basicSalaryType
    ? `${
        basicSalaryEnumTmp[jd.basicSalaryType]
      } ${minSalaryText}${maxSalaryText}`
    : '';

  html += titleWithDescription(
    '雇用形態',
    jd.employmentPatternNote ?? jd.jobDescriptionEmploymentPattern?.name ?? ''
  );

  html += titleWithDescription('試用期間', jd.trialPeriod ?? '');

  if (jd.basicSalaryType && typeof jd.basicSalaryMin === 'number') {
    html += titleWithDescription('給与', salaryText);
  } else {
    html += titleWithDescription('給与', jd.salary ?? '');
  }
  html += titleWithDescription('賞与', jd.bonus ?? '');

  html += titleWithDescription(
    '年収下限（万円）',
    jd.numericSalaryMin ? `${jd.numericSalaryMin * 0.0001}` : ''
  );
  html += titleWithDescription(
    '年収上限（万円）',
    jd.numericSalaryMax ? `${jd.numericSalaryMax * 0.0001}` : ''
  );
  html += titleWithDescription('給与例', jd.salaryExamples ?? '');
  html += titleWithDescription('勤務地', jd.location ?? '');
  html += titleWithDescription('勤務時間', jd.workHours ?? '');
  html += titleWithDescription('休憩時間', jd.breakTime ?? '');
  html += titleWithDescription('時間外労働', jd.overtimeWork ?? '');
  html += titleWithDescription('休日・有給休暇・特別休暇', jd.holidays ?? '');
  html += titleWithDescription('福利厚生', jd.welfare ?? '');
  html += titleWithDescription('諸手当', jd.otherBenefits ?? '');
  html += titleWithDescription('加入保険', jd.insurance ?? '');
  html += titleWithDescription(
    '就業場所における受動喫煙を防止するための措置',
    jd.smokingPolicy ?? ''
  );
  html += titleWithDescription(
    '教育・スキルアップ・キャリアアップ',
    jd.careerUpPrograms ?? ''
  );
  html += titleWithDescription('健康', jd.health ?? '');
  html += titleWithDescription('面接・選考プロセス', jd.screeningProcess ?? '');
  html += titleWithDescription('その他', jd.others ?? '');

  return html;
};

const job_name = (
  job_type_name?: string,
  management_job_type_name?: string
) => {
  if (job_type_name && management_job_type_name) {
    return `${job_type_name} & ${management_job_type_name}`;
  } else if (job_type_name && !management_job_type_name) {
    return job_type_name;
  } else if (!job_type_name && management_job_type_name) {
    return management_job_type_name;
  }
  return '';
};

const createJobPostingJson = (props?: Props) => {
  if (!props) return {};

  const { jobDescription, company } = props;
  const descriptionHtml = createJobDescriptionHtml(props);

  return {
    '@context': 'http://schema.org',
    '@type': 'JobPosting',
    title: job_name(
      jobDescription.jobType?.name,
      jobDescription.managementJobType?.name
    ),
    description: descriptionHtml,
    hiringOrganization: {
      '@type': 'Organization',
      name: company.name,
      url: company.url,
      image: company.imageUrl,
      logo: company.location,
    },
    datePosted: formatDate(jobDescription.createdAt, 'yyyy-MM-dd'),
    employmentType: 'FULL_TIME',
    jobLocation: {
      '@type': 'Place',
      address: company.location,
    },
    occupationalCategory: job_name(
      jobDescription.jobType?.name,
      jobDescription.managementJobType?.name
    ),
    isAccessibleForFree: false,
    hasPart: {
      '@type': 'WebPageElement',
      isAccessibleForFree: false,
      cssSelector: '.paywall',
    },
  };
};

export const JsonLdTag = (props: Props) => {
  const json = createJobPostingJson(props);

  return (
    <Head>
      <script
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(json),
        }}
        type="application/ld+json"
      />
    </Head>
  );
};
